import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { OverrideStyleProps } from '..'
import { doNothing } from '../../Content/helpers/functions/doNothing'
import { getModuleIds } from '../../Content/helpers/functions/getModuleIds'
import {
  GetSectionPostOptionsProps,
  OnDragDropProps,
  OnShowAssetPickerProps,
  SectionPostOptions,
} from '../../Content/helpers/types'
import type { ModuleDefinition, Module } from '../../Content/helpers/types'
import { CarouselSection } from '../CarouselSection'
import { styles as sharedStyles } from '../sharedStyles/styles'
import { CarouselArrows } from './CarouselArrows'
import { styles } from './styles'

export interface ScrollableCarouselBodyProps {
  allProps: Record<string, unknown>
  availableModules: ModuleDefinition[]
  carouselWidth: number
  currentIndex: number
  getModule?: (args: GetSectionPostOptionsProps) => SectionPostOptions
  isPresenting: boolean
  isTooltipActive: boolean
  modules: Module[]
  overrideStyles?: OverrideStyleProps
  onDeleteSection: (indexToRemove: number) => void
  onDragDrop: OnDragDropProps
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  onTooltipToggle: (index: number) => void
  readOnly: boolean
  setCurrentIndex: (index: number) => void
  setIsTooltipActive: (activeTooltipIndex: number) => void
  updateSectionPostOption: (index: number, value: string, name: string) => void
}

export const ScrollableCarouselBody: React.FC<ScrollableCarouselBodyProps> = ({
  allProps,
  availableModules,
  carouselWidth,
  currentIndex,
  getModule,
  isPresenting,
  isTooltipActive,
  modules,
  onDeleteSection,
  overrideStyles,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  onTooltipToggle,
  readOnly,
  setCurrentIndex,
  setIsTooltipActive,
  updateSectionPostOption,
}) => {
  const leftArrowDisabled = currentIndex - 1 === -1
  const rightArrowDisabled = currentIndex + 1 >= modules.length
  const timer = useRef<NodeJS.Timeout>()
  const [shouldScroll, setShouldScroll] = useState<string | null>(null)
  const modulesLength = useRef(modules.length)
  const moduleIds = useRef(getModuleIds(modules))

  // Scrolls to new desired index by button press
  // or by section position option change
  const onPositionChange = useCallback(
    (index: number, length: number) => {
      setIsTooltipActive(-1)
      let newIndex = index
      if (newIndex >= length) newIndex = length - 1
      if (newIndex < 0) newIndex = 0
      setCurrentIndex(newIndex)
    },
    [setCurrentIndex, setIsTooltipActive]
  )

  const handleGoBack = () => {
    // slides carousel backward by one
    if (!leftArrowDisabled) {
      setShouldScroll('right')
      timer.current = setTimeout(() => {
        onPositionChange(currentIndex - 1, modules.length)
        setShouldScroll(null)
      }, 500)
    }
  }

  const handleGoForward = () => {
    // slides carousel forward by one
    if (!rightArrowDisabled) {
      setShouldScroll('left')
      timer.current = setTimeout(() => {
        onPositionChange(currentIndex + 1, modules.length)
        setShouldScroll(null)
      }, 500)
    }
  }

  const handleDeleteSection = (index: number) => {
    const newIndex = index - 1 < 0 ? 0 : index - 1
    setShouldScroll(index === 0 ? 'left' : 'right')
    timer.current = setTimeout(() => {
      onDeleteSection(index)
      moduleIds.current = getModuleIds(modules)
      onPositionChange(newIndex, modules.length)
      setShouldScroll(null)
    }, 500)
  }

  const getSectionKey = (index: number, sectionId?: string) => {
    const name = modules[index].componentName || modules[index].name || ''
    const moduleId = moduleIds.current ? moduleIds.current[index] : 0
    return `${name}-${sectionId || index}-${moduleId}`
  }

  useEffect(() => {
    return () => clearTimeout(timer.current)
  }, [])

  useEffect(() => {
    if (modulesLength.current < modules.length) {
      setShouldScroll('new-left')
      modulesLength.current = modules.length
      timer.current = setTimeout(() => {
        onPositionChange(modules.length, modules.length)
        setShouldScroll(null)
      }, 500)
    }
  }, [modules.length, onPositionChange])

  return (
    <Fragment>
      <div
        css={styles.getViewport({
          width: carouselWidth,
          isAnimated: Boolean(shouldScroll) && shouldScroll !== 'none',
        })}
      >
        <ol css={styles.scrollWrapper}>
          {modules[currentIndex - 1] && (
            <li key={getSectionKey(currentIndex - 1)}>
              <div
                className={kuiperClassNames('modular-carousel-viewport')}
                css={styles.getAnimationViewPort(
                  overrideStyles?.viewportStyles
                    ? overrideStyles.viewportStyles()
                    : {}
                )}
              >
                <div
                  css={styles.getLeftCarouselSection(
                    carouselWidth,
                    readOnly,
                    shouldScroll === 'right',
                    overrideStyles?.sectionStyles
                      ? overrideStyles.sectionStyles()
                      : {}
                  )}
                >
                  <CarouselSection
                    allProps={allProps}
                    availableModules={availableModules}
                    disableSectionOptions={true}
                    index={currentIndex - 1}
                    isPresenting={isPresenting}
                    module={modules[currentIndex - 1]}
                    modules={modules || []}
                    onDeleteSection={doNothing}
                    onDragDrop={doNothing}
                    onExpandAsset={doNothing}
                    onRemoveAsset={doNothing}
                    onShowAssetPicker={doNothing}
                    onTooltipToggle={doNothing}
                    readOnly={readOnly}
                    tooltipActive={false}
                    updateSectionOptions={doNothing}
                    width={carouselWidth}
                  />
                </div>
              </div>
            </li>
          )}
          {modules[currentIndex] && (
            <Fragment>
              <li
                key={getSectionKey(currentIndex)}
                className={kuiperClassNames('asset-carousel')}
              >
                {modules && modules.length && (
                  <div
                    css={sharedStyles.getIndexIndicator(
                      false,
                      carouselWidth,
                      readOnly || isPresenting
                    )}
                  >
                    {currentIndex + 1} of {modules.length}
                  </div>
                )}
                <div
                  css={styles.getCenterCarouselSection(
                    carouselWidth,
                    !overrideStyles?.viewportStyles
                      ? shouldScroll || 'none'
                      : 'none'
                  )}
                >
                  <CarouselSection
                    allProps={allProps}
                    availableModules={availableModules}
                    disableSectionOptions={false}
                    getModule={getModule}
                    index={currentIndex}
                    isPresenting={isPresenting}
                    module={modules[currentIndex]}
                    modules={modules || []}
                    onDeleteSection={(index: number) =>
                      handleDeleteSection(index)
                    }
                    onDragDrop={onDragDrop}
                    onExpandAsset={onExpandAsset}
                    onRemoveAsset={onRemoveAsset}
                    onShowAssetPicker={onShowAssetPicker}
                    onTooltipToggle={(index) => onTooltipToggle(index)}
                    readOnly={readOnly}
                    tooltipActive={isTooltipActive}
                    updateSectionOptions={updateSectionPostOption}
                    width={carouselWidth}
                  />
                </div>
              </li>
              {overrideStyles?.viewportStyles && (
                <li
                  key={getSectionKey(currentIndex, 'viewport')}
                  className={kuiperClassNames('asset-carousel')}
                >
                  <Fragment>
                    <div
                      className={kuiperClassNames('modular-carousel-viewport')}
                      css={styles.getAnimationViewPort(
                        overrideStyles?.viewportStyles
                          ? overrideStyles.viewportStyles()
                          : {},
                        overrideStyles?.activeViewportStyles
                          ? overrideStyles.activeViewportStyles()
                          : {}
                      )}
                    >
                      <div
                        css={styles.getCenterCarouselSection(
                          carouselWidth,
                          shouldScroll || 'none',
                          overrideStyles?.sectionStyles
                            ? overrideStyles.sectionStyles()
                            : {}
                        )}
                      >
                        <CarouselSection
                          allProps={allProps}
                          availableModules={availableModules}
                          disableSectionOptions={false}
                          index={currentIndex}
                          isPresenting={isPresenting}
                          module={modules[currentIndex]}
                          modules={modules || []}
                          onDeleteSection={(index: number) =>
                            handleDeleteSection(index)
                          }
                          onDragDrop={onDragDrop}
                          onExpandAsset={onExpandAsset}
                          onRemoveAsset={onRemoveAsset}
                          onShowAssetPicker={onShowAssetPicker}
                          onTooltipToggle={(index) => onTooltipToggle(index)}
                          readOnly={readOnly}
                          tooltipActive={isTooltipActive}
                          updateSectionOptions={updateSectionPostOption}
                          width={carouselWidth}
                        />
                      </div>
                    </div>
                  </Fragment>
                </li>
              )}
              <li key={`new-component-${modules.length}`}>
                <div
                  className={kuiperClassNames('modular-carousel-viewport')}
                  css={styles.getAnimationViewPort(
                    overrideStyles?.viewportStyles
                      ? overrideStyles.viewportStyles()
                      : {}
                  )}
                >
                  <div
                    css={styles.getRightCarouselSection(
                      carouselWidth,
                      readOnly,
                      shouldScroll === 'new-left',
                      overrideStyles?.sectionStyles
                        ? overrideStyles.sectionStyles()
                        : {}
                    )}
                  >
                    <CarouselSection
                      allProps={allProps}
                      availableModules={availableModules}
                      disableSectionOptions={true}
                      index={modules.length - 1}
                      isPresenting={isPresenting}
                      module={modules[modules.length - 1]}
                      modules={modules || []}
                      onDeleteSection={doNothing}
                      onDragDrop={doNothing}
                      onExpandAsset={doNothing}
                      onRemoveAsset={doNothing}
                      onShowAssetPicker={doNothing}
                      onTooltipToggle={doNothing}
                      readOnly={readOnly}
                      tooltipActive={false}
                      updateSectionOptions={doNothing}
                      width={carouselWidth}
                    />
                  </div>
                </div>
              </li>
            </Fragment>
          )}
          {modules[currentIndex + 1] && (
            <li key={getSectionKey(currentIndex + 1)}>
              <div
                className={kuiperClassNames('modular-carousel-viewport')}
                css={styles.getAnimationViewPort(
                  overrideStyles?.viewportStyles
                    ? overrideStyles.viewportStyles()
                    : {}
                )}
              >
                <div
                  css={styles.getRightCarouselSection(
                    carouselWidth,
                    readOnly,
                    shouldScroll === 'left',
                    overrideStyles?.sectionStyles
                      ? overrideStyles.sectionStyles()
                      : {}
                  )}
                >
                  <CarouselSection
                    allProps={allProps}
                    availableModules={availableModules}
                    disableSectionOptions={true}
                    index={currentIndex + 1}
                    isPresenting={isPresenting}
                    module={modules[currentIndex + 1]}
                    modules={modules || []}
                    onDeleteSection={doNothing}
                    onDragDrop={doNothing}
                    onExpandAsset={doNothing}
                    onRemoveAsset={doNothing}
                    onShowAssetPicker={doNothing}
                    onTooltipToggle={doNothing}
                    readOnly={readOnly}
                    tooltipActive={false}
                    updateSectionOptions={doNothing}
                    width={carouselWidth}
                  />
                </div>
              </div>
            </li>
          )}
        </ol>
      </div>
      <CarouselArrows
        carouselWidth={carouselWidth}
        currentIndex={currentIndex}
        overrideStyles={overrideStyles}
        modulesLength={modules.length || 0}
        readOnly={readOnly}
        onGoBack={handleGoBack}
        onGoForward={handleGoForward}
      />
    </Fragment>
  )
}
